<template>
  <div class="invoice-history-component">
    <LoadingIndicatorComponent v-if="isLoading" />
    <PlaceholderCardComponent
      v-else-if="invoices.length === 0"
      :title="$t('invoiceHistory.noInvoices')"
      :subtitle="$t('invoiceHistory.notificationInfo')"
    />
    <template v-else>
      <div class="d-flex justify-content-end mb-2 export-toolbar">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="isExporting"
          @click="exportInvoices"
        >
          <FontAwesomeIcon
            v-if="isExporting"
            icon="spinner"
            spin
            class="me-1"
          />
          <FontAwesomeIcon v-else icon="download" class="me-1" />
          {{ $t("invoiceHistory.export_xlsx") }}
        </button>
      </div>
      <div
        class="d-flex-row border rounded-3 overflow-hidden border-gray-200 fs-9"
      >
        <!-- Header row -->
        <div
          class="row bg-secondary-subtle mb-2 fw-bold d-none d-lg-flex p-1 g-0 align-items-center"
        >
          <div v-if="is_admin" class="col-1 p-1 text-center">
            <p class="mb-0">Invoice ID</p>
          </div>
          <div v-if="is_admin" class="col-1 p-1 text-center">
            <p class="mb-0">Admin actions</p>
          </div>
          <div v-if="showCompanyColumn" class="col-2 p-1">
            <p class="mb-0">{{ $t("common.company") }}</p>
          </div>
          <div class="col p-1">
            <p class="mb-0">{{ $t("invoice.number") }}</p>
          </div>
          <div class="col-1 p-1 text-center">
            <p class="mb-0">{{ $t("invoice.status_label") }}</p>
          </div>
          <div class="col-2 p-1 text-center">
            <p class="mb-0">{{ $t("invoice.requested_date") }}</p>
          </div>
          <div class="col-1 p-1 text-end">
            <p class="mb-0">{{ $t("invoice.amount") }}</p>
          </div>
          <div class="col-2 p-1 text-center">
            <p class="mb-0">{{ $t("document.download") }}</p>
          </div>
        </div>

        <!-- Invoice rows -->
        <InvoiceDisplayComponent
          v-for="invoice in invoices"
          :key="invoice.id"
          :invoice="invoice"
          :is_admin="is_admin"
          :show-company-column="showCompanyColumn"
          :company-name="getCompanyName(invoice.company_id)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import InvoiceDisplayComponent from "@/money/invoice/InvoiceDisplayComponent.vue";
import { Company, Invoice } from "@/types";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { computed, ref, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = withDefaults(
  defineProps<{
    company_id?: number;
    companies?: Company[];
    is_admin: boolean;
  }>(),
  { companies: () => [] }
);

const invoices = ref<Invoice[]>([]);
const isLoading = ref(false);
const isExporting = ref(false);
const repeatedArrayParamsSerializer = { indexes: null };
const { globalErrorHandler } = useGlobalHandlers();
let fetchRequestId = 0;

const showCompanyColumn = computed(() => {
  return (
    props.companies.length > 1 ||
    (props.companies.length === 0 && !props.company_id)
  );
});

const companyIds = computed(() => {
  if (props.companies.length > 0) {
    return props.companies
      .map((company) => company.id)
      .filter(Boolean) as number[];
  } else if (props.company_id) {
    return [props.company_id];
  }
  return [];
});

const getCompanyName = (companyId: number) => {
  const company = props.companies.find((company) => company.id === companyId);
  return company ? company.name : "";
};

const sortInvoicesByRequestedAt = (allInvoices: Invoice[]) => {
  return [...allInvoices].sort((a: Invoice, b: Invoice) => {
    if (!a.requested_at) return -1;
    if (!b.requested_at) return 1;
    return (
      new Date(b.requested_at).getTime() - new Date(a.requested_at).getTime()
    );
  });
};

async function getInvoicesForCompany(companyId: number): Promise<Invoice[]> {
  const response = await goSolidApi.get(`/api/company/${companyId}/invoices`);
  if (response.status !== 200) {
    throw new Error("Error fetching invoices");
  }

  return response.data.invoices.map((invoice: Invoice) => ({
    ...invoice,
    company_id: companyId,
  }));
}

// The consolidated view refetches every time companies are (de)selected, so
// responses can arrive out of order: an earlier, slower request must neither
// overwrite newer invoices nor clear the spinner while the newer one is still
// in flight.
async function fetchInvoices() {
  const currentRequestId = ++fetchRequestId;
  const requestedCompanyId = props.company_id;
  isLoading.value = true;
  try {
    if (companyIds.value.length === 0) {
      invoices.value = [];
      return;
    }

    // Single-company views pass an explicit `company_id` and use the
    // per-company endpoint. The consolidated view passes a `companies` list
    // (no `company_id`) and always uses the multi-company endpoint, so the
    // result is consistent no matter how many companies are selected.
    if (requestedCompanyId) {
      const companyInvoices = await getInvoicesForCompany(requestedCompanyId);
      if (currentRequestId !== fetchRequestId) return;
      invoices.value = sortInvoicesByRequestedAt(companyInvoices);
      return;
    }

    const response = await goSolidApi.get("/api/companies/invoices", {
      params: { company_ids: companyIds.value },
      paramsSerializer: repeatedArrayParamsSerializer,
    });
    if (currentRequestId !== fetchRequestId) return;
    const groupedInvoices = response.data.invoices ?? {};
    const allInvoices = Object.entries(groupedInvoices).flatMap(
      ([companyId, companyInvoices]) =>
        (companyInvoices as Invoice[]).map((invoice) => ({
          ...invoice,
          company_id: Number(companyId),
        }))
    );
    invoices.value = sortInvoicesByRequestedAt(allInvoices);
  } catch (error: unknown) {
    if (currentRequestId !== fetchRequestId) return;
    globalErrorHandler(error);
    invoices.value = [];
  } finally {
    if (currentRequestId === fetchRequestId) {
      isLoading.value = false;
    }
  }
}

async function exportInvoices() {
  if (isExporting.value || companyIds.value.length === 0) return;
  isExporting.value = true;
  try {
    const response = await goSolidApi.get("/api/companies/invoices/export", {
      params: { company_ids: companyIds.value },
      paramsSerializer: repeatedArrayParamsSerializer,
      responseType: "blob",
    });
    downloadFromApiResponse(
      response.data,
      response.headers,
      "soldera_invoices_export.xlsx"
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isExporting.value = false;
  }
}

watch(companyIds, fetchInvoices, { immediate: true });
</script>

<style scoped lang="scss"></style>
